// The three masthead cells: who is doing the work, who it is for, and the rates
// and identity the whole sheet is priced and filed under.
import { useTranslation } from 'react-i18next';
import {
  LOGISTICS_CALCULATION_SHEET_CHROME,
  formatSheetAmount,
  type LogisticsCalculationPublicDto,
} from '@magimp/shared';
import { DocText, useDocumentEditing } from './DocumentCell.js';
import { formatSheetDate, rubPerCnyRate } from './document-format.js';

const chrome = LOGISTICS_CALCULATION_SHEET_CHROME;
const doc = chrome.document;
const NUMBER_SIGN = '№';
// The masthead names the document by the first word of its own frozen title, so
// the printed sheet stays Russian whatever language the operator works in.
const [DOCUMENT_WORD = ''] = chrome.title.split(' ');

type Dto = LogisticsCalculationPublicDto | null;

export function DocumentMasthead({ dto, publicCode }: { dto: Dto; publicCode: string }) {
  return (
    <table className="doc-head" data-testid="rl-document-masthead">
      <tbody>
        <tr>
          <ExecutorCell dto={dto} />
          <CustomerCell dto={dto} />
          <IdentityCell dto={dto} publicCode={publicCode} />
        </tr>
      </tbody>
    </table>
  );
}

function ExecutorCell({ dto }: { dto: Dto }) {
  const { t } = useTranslation('logisticsCalculations');
  return (
    <td>
      <div className="doc-k">{chrome.executorLabel}</div>
      <div className="doc-v">{chrome.executorName}</div>
      <div className="doc-add">
        <DocText
          name="managerName"
          label={t('editor.fields.managerName')}
          value={dto?.manager.name ?? ''}
        />
        {' · '}
        <span className="doc-n">
          <DocText
            name="managerPhone"
            label={t('editor.fields.managerPhone')}
            value={dto?.manager.phone ?? ''}
          />
        </span>
        <ManagerTelegram telegram={dto?.manager.telegram ?? null} />
      </div>
    </td>
  );
}

/**
 * The handle is optional on the wire, so the printed cell drops the clause
 * entirely while the editing cell always offers the field that fills it in.
 */
function ManagerTelegram({ telegram }: { telegram: string | null }) {
  const { t } = useTranslation('logisticsCalculations');
  const editing = useDocumentEditing();
  if (!editing && !telegram) return null;
  const field = (
    <DocText
      name="managerTelegram"
      label={t('editor.fields.managerTelegram')}
      value={telegram ?? ''}
    />
  );
  // While filling in, the handle takes its own line under the name and phone;
  // the printed clause stays inside the one-line sentence.
  if (editing) {
    return (
      <span className="doc-tg">
        {`${doc.telegramLabel}: `}
        {field}
      </span>
    );
  }
  return (
    <>
      {` · ${doc.telegramLabel}: `}
      {field}
    </>
  );
}

function CustomerCell({ dto }: { dto: Dto }) {
  const { t } = useTranslation('logisticsCalculations');
  return (
    <td>
      <div className="doc-k">{chrome.customerLabel}</div>
      <div className="doc-v">
        <DocText
          name="clientCompany"
          label={t('editor.fields.clientCompany')}
          value={dto?.client.company ?? ''}
        />
      </div>
      <div className="doc-add">
        <DocText
          name="clientContactName"
          label={t('editor.fields.clientContactName')}
          value={dto?.client.contactName ?? ''}
        />
        {' · '}
        <span className="doc-n">
          <DocText
            name="clientContactPhone"
            label={t('editor.fields.clientContactPhone')}
            value={dto?.client.contactPhone ?? ''}
          />
        </span>
      </div>
    </td>
  );
}

function IdentityCell({ dto, publicCode }: { dto: Dto; publicCode: string }) {
  const { t } = useTranslation('logisticsCalculations');
  return (
    <td>
      <div className="doc-k">{doc.rateCaption}</div>
      <div className="doc-v doc-n">
        <RateValue dto={dto} />
      </div>
      <div className="doc-add doc-n">
        {`${DOCUMENT_WORD} ${NUMBER_SIGN} ${publicCode} · ${doc.dueCaption} `}
        <DocText
          name="payDueDate"
          label={t('editor.fields.payDueDate')}
          type="date"
          value={dto ? formatSheetDate(dto.payDueDate) : ''}
        />
      </div>
    </td>
  );
}

/**
 * The customer reads what one dollar and one yuan cost in roubles, and the
 * manager types the same two rates — the way they are quoted, with nothing left
 * to divide by hand.
 *
 * WHY the yuan input keeps a placeholder: a document stored before this sheet
 * asked for the rate has none to show, so the derivation the customer's copy
 * already printed stands in for it until the manager states the real one. It is
 * display only — an untouched input writes no rate.
 */
function RateValue({ dto }: { dto: Dto }) {
  const { t } = useTranslation('logisticsCalculations');
  const editing = useDocumentEditing();
  const derivedPerCny = rubPerCnyRate(dto?.rubPerUsdRate, dto?.cnyPerUsdRate);
  if (editing) {
    return (
      <>
        {'$1 = '}
        <DocText
          name="rubPerUsdRate"
          label={t('editor.fields.rubPerUsdRate')}
          value={dto?.rubPerUsdRate ?? ''}
          numeric
        />
        {' ₽ · ¥1 = '}
        <DocText
          name="rubPerCnyRate"
          label={t('editor.fields.rubPerCnyRate')}
          value={dto?.rubPerCnyRate ?? ''}
          numeric
          placeholder={derivedPerCny ?? undefined}
        />
        {' ₽'}
      </>
    );
  }
  const perCny = dto?.rubPerCnyRate ?? derivedPerCny;
  const parts: string[] = [];
  if (dto?.rubPerUsdRate) parts.push(`$1 = ${formatSheetAmount(dto.rubPerUsdRate, 'RUB')}`);
  if (perCny !== null) parts.push(`¥1 = ${formatSheetAmount(perCny, 'RUB')}`);
  return <>{parts.join(' · ')}</>;
}
